import { Button, Col, Row, Select } from 'antd';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import {
  DonorStatus,
  DvmExamListFilterInput,
  useGetLocationsQuery,
  useGetRegionsQuery,
  useGetUsersListQuery,
  UserRole,
} from 'gql/types';
import { RangeDatePicker } from 'modules/common/components';
import moment, { Moment } from 'moment';
import { FC, useMemo, useState } from 'react';
import { getTranslation } from 'utils/getTranslation';

import styles from './styles.module.scss';

const t = getTranslation('donor.GeneralDvmExamTable.FilterBar');

const formatStatusLabel = (status: string) =>
  status.replace(/([a-z])([A-Z])/g, '$1 $2');

const STATUS_OPTIONS = Object.values(DonorStatus).map((status) => ({
  value: status,
  label: formatStatusLabel(status),
}));

// "John A. Smith" and "John Smith" are the same vet: key on the first
// given-name token + last name, ignoring middle initials and periods
const vetGroupKey = (firstName: string, lastName: string) => {
  const first = firstName.trim().split(/\s+/)[0] ?? '';
  return `${first} ${lastName.trim()}`.replace(/\./g, '').toLowerCase();
};

const vetGroupLabel = (firstName: string, lastName: string) => {
  const first = firstName.trim().split(/\s+/)[0] ?? '';
  return `${first} ${lastName.trim()}`.replace(/\./g, '');
};

export type DueDatePreset = 30 | 90;

type FilterBarProps = {
  filter: DvmExamListFilterInput;
  onFilterChange: (
    fieldName: keyof DvmExamListFilterInput,
    value: unknown
  ) => void;
  onClear: () => void;
};

export const FilterBar: FC<FilterBarProps> = ({
  filter,
  onFilterChange,
  onClear,
}) => {
  const [activePreset, setActivePreset] = useState<DueDatePreset | null>(null);

  const regionsQuery = useGetRegionsQuery();
  const locationsQuery = useGetLocationsQuery({
    variables: {
      input: {
        take: 1000,
        filter: {
          regionIds: filter.regionIds?.length ? filter.regionIds : undefined,
        },
      },
    },
  });
  const vetsQuery = useGetUsersListQuery({
    variables: {
      input: {
        take: 1000,
        filter: {
          roles: [UserRole.Dvm, UserRole.MedicalDirector],
        },
      },
    },
  });

  const regionOptions = useMemo(
    () =>
      (regionsQuery.data?.getRegions || []).map(({ id, name }) => ({
        value: id,
        label: name,
      })),
    [regionsQuery.data]
  );

  const locationOptions = useMemo(
    () =>
      (locationsQuery.data?.getLocations.data || []).map(({ id, name }) => ({
        value: id,
        label: name,
      })),
    [locationsQuery.data]
  );

  const vetOptions = useMemo(() => {
    const groups = new Map<string, { label: string; ids: string[] }>();
    (vetsQuery.data?.getUsersList.data || []).forEach(
      ({ id, firstName, lastName }) => {
        const key = vetGroupKey(firstName, lastName);
        const group = groups.get(key);
        if (group) {
          group.ids.push(id);
        } else {
          groups.set(key, {
            label: vetGroupLabel(firstName, lastName),
            ids: [id],
          });
        }
      }
    );
    return [...groups.values()]
      .map(({ label, ids }) => ({ value: ids.sort().join(','), label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [vetsQuery.data]);

  const setDueWindow = (from: Date | null, to: Date | null) => {
    onFilterChange('nextExamDueFrom', from ? from.toISOString() : undefined);
    onFilterChange('nextExamDueTo', to ? to.toISOString() : undefined);
  };

  const handlePresetClick = (preset: DueDatePreset) => {
    if (activePreset === preset) {
      setActivePreset(null);
      setDueWindow(null, null);
      return;
    }
    setActivePreset(preset);
    const today = new Date();
    setDueWindow(startOfDay(today), endOfDay(addDays(today, preset)));
  };

  const handleRangeChange = (dates: [Moment | null, Moment | null] | null) => {
    setActivePreset(null);
    const [from, to] = dates ?? [null, null];
    setDueWindow(
      from ? startOfDay(from.toDate()) : null,
      to ? endOfDay(to.toDate()) : null
    );
  };

  const rangeValue: [Moment | null, Moment | null] | null =
    activePreset === null && (filter.nextExamDueFrom || filter.nextExamDueTo)
      ? [
          filter.nextExamDueFrom ? moment(filter.nextExamDueFrom) : null,
          filter.nextExamDueTo ? moment(filter.nextExamDueTo) : null,
        ]
      : null;

  const hasActiveFilters = Boolean(
    filter.nextExamDueFrom ||
      filter.nextExamDueTo ||
      filter.donorStatuses?.length ||
      filter.regionIds?.length ||
      filter.locationIds?.length ||
      filter.performedById ||
      filter.performedByIds?.length
  );

  const handleClear = () => {
    setActivePreset(null);
    onClear();
  };

  return (
    <Row gutter={[8, 8]} align="bottom">
      <Col>
        <Button
          type={activePreset === 30 ? 'primary' : 'default'}
          onClick={() => handlePresetClick(30)}
        >
          {t('dueNext30')}
        </Button>
      </Col>
      <Col>
        <Button
          type={activePreset === 90 ? 'primary' : 'default'}
          onClick={() => handlePresetClick(90)}
        >
          {t('dueNext90')}
        </Button>
      </Col>
      <Col>
        <span className={styles.filterLabel}>{t('nextExamDueLabel')}</span>
        <RangeDatePicker
          value={rangeValue as any}
          onChange={handleRangeChange as any}
          placeholder={[t('dueFrom'), t('dueTo')]}
          allowEmpty={[true, true]}
        />
      </Col>
      <Col style={{ minWidth: 180 }}>
        <span className={styles.filterLabel}>{t('statusLabel')}</span>
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          maxTagCount="responsive"
          style={{ width: '100%' }}
          placeholder={t('allStatuses')}
          options={STATUS_OPTIONS}
          value={filter.donorStatuses ?? []}
          onChange={(value: DonorStatus[]) =>
            onFilterChange('donorStatuses', value.length ? value : undefined)
          }
        />
      </Col>
      <Col style={{ minWidth: 180 }}>
        <span className={styles.filterLabel}>{t('regionLabel')}</span>
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          maxTagCount="responsive"
          style={{ width: '100%' }}
          placeholder={t('allRegions')}
          loading={regionsQuery.loading}
          options={regionOptions}
          value={filter.regionIds ?? []}
          onChange={(value: string[]) =>
            onFilterChange('regionIds', value.length ? value : undefined)
          }
        />
      </Col>
      <Col style={{ minWidth: 180 }}>
        <span className={styles.filterLabel}>{t('locationLabel')}</span>
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          maxTagCount="responsive"
          style={{ width: '100%' }}
          placeholder={t('allLocations')}
          loading={locationsQuery.loading}
          options={locationOptions}
          value={filter.locationIds ?? []}
          onChange={(value: string[]) =>
            onFilterChange('locationIds', value.length ? value : undefined)
          }
        />
      </Col>
      <Col style={{ minWidth: 180 }}>
        <span className={styles.filterLabel}>{t('vetLabel')}</span>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: '100%' }}
          placeholder={t('allVets')}
          loading={vetsQuery.loading}
          options={vetOptions}
          value={
            filter.performedByIds?.length
              ? [...filter.performedByIds].sort().join(',')
              : undefined
          }
          onChange={(value?: string) =>
            onFilterChange(
              'performedByIds',
              value ? value.split(',') : undefined
            )
          }
        />
      </Col>
      {hasActiveFilters && (
        <Col>
          <Button type="link" onClick={handleClear}>
            {t('clearFilters')}
          </Button>
        </Col>
      )}
    </Row>
  );
};
